export type RequestAuthContext = {
  email?: string;
  accessToken?: string;
};

export type RequestRuntimeConfig = {
  environment?: string;
  analyticsServerURL?: string;
  licenseAndUpdateBaseUrl?: string;
  cortexStudioUrl?: string;
};

type RequestContextProviders = {
  getAuth?: () => RequestAuthContext;
  // `audience` (when provided) requests an Auth0 access token whose `aud`
  // claim matches that API. Auth0 mints one JWT per audience; the SDK
  // caches each independently. Used to call APIs that validate against
  // a different audience than the default (e.g. cortex-research-api).
  getAccessToken?: (audience?: string) => Promise<string | undefined>;
  getRuntime?: () => RequestRuntimeConfig;
  onAuthFailure?: () => void;
};

let getAuthContext: () => RequestAuthContext = () => {
  return {
    email: undefined,
    accessToken: undefined,
  };
};

let getGroupContext: () => { defaultGroupId: string } = () => {
  return {
    defaultGroupId: '',
  };
};

let getAccessToken: (audience?: string) => Promise<string | undefined> = async () => {
  const auth = getAuthContext();
  return auth.accessToken;
};

// Cortex-studio + research-api validate JWTs against this audience. The
// default Auth0 audience used elsewhere (VITE_AUTH0_AUDIENCE, e.g.
// api-prod.omnibusx.com) doesn't match, so cortex-bound requests must
// fetch a separately-audienced token. Override at build time if you
// point the stack at a different Auth0 tenant.
const CORTEX_AUDIENCE: string | undefined = import.meta.env.VITE_CORTEX_AUDIENCE;

function audienceForServer(server: Servers): string | undefined {
  if (server === Servers.CortexStudio) return CORTEX_AUDIENCE;
  return undefined; // default audience (set on the SDK at boot)
}

let getRuntimeConfig: () => RequestRuntimeConfig = () => {
  return {
    environment: undefined,
    pythonServerPort: undefined,
    analyticsServerURL: undefined,
    licenseAndUpdateBaseUrl: undefined,
    localPythonBaseUrl: undefined,
    cortexStudioUrl: undefined,
  };
};

let onAuthFailure: () => void = () => {
  window.location.href = window.location.origin;
};
let authFailureTriggered = false;

export function configureRequests(providers: RequestContextProviders) {
  if (providers.getAuth) {
    getAuthContext = providers.getAuth;
  }
  if (providers.getAccessToken) {
    getAccessToken = providers.getAccessToken;
  }
  if (providers.getRuntime) {
    getRuntimeConfig = providers.getRuntime;
  }
  if (providers.onAuthFailure) {
    onAuthFailure = providers.onAuthFailure;
  }
}

function handleUnauthorized<T>(): Promise<T> {
  if (!authFailureTriggered) {
    authFailureTriggered = true;
    onAuthFailure();
  }
  // Never resolve — page is redirecting to login
  return new Promise(() => {});
}

export function configureGroupContext(groupID: string) {
  getGroupContext = () => {
    return {
      defaultGroupId: groupID,
    };
  };
}

function buildAuthHeaders(): Record<string, string> {
  const auth = getAuthContext();
  const headers: Record<string, string> = {};

  if (auth.email) {
    headers['OmnibusX-Email'] = auth.email;
  }
  headers['OmnibusX-GroupId'] = getGroupContext().defaultGroupId;

  return headers;
}

async function buildAuthorizationHeader(audience?: string): Promise<Record<string, string>> {
  // Skip the auth-context shortcut when an explicit audience is requested
  // — the cached accessToken on the auth context is for the default
  // audience and won't be accepted by APIs requiring a different one.
  const tokenFromAuthContext = audience ? undefined : getAuthContext().accessToken;
  const token = tokenFromAuthContext ?? (await getAccessToken(audience));
  if (!token) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

async function buildRequestHeaders(
  extraHeaders: Record<string, string> = {},
  options: { includeContentType?: boolean; audience?: string } = {},
): Promise<Record<string, string>> {
  const includeContentType = options.includeContentType ?? true;
  return {
    ...(includeContentType ? { 'Content-Type': 'application/json' } : {}),
    ...buildAuthHeaders(),
    ...(await buildAuthorizationHeader(options.audience)),
    ...extraHeaders,
  };
}

export const getHeaders = (extraHeaders: Record<string, string> = {}) => {
  // Only build static headers without async Authorization - for image requests ONLY
  return {
    'Content-Type': 'application/json',
    ...buildAuthHeaders(),
    ...extraHeaders,
  };
};

export enum Servers {
  LicenseAndUpdate,
  LocalPython,
  AnalyticServer,
  CortexStudio,
}

export const buildUrl = (server: Servers, endpoint: string) => {
  const runtime = getRuntimeConfig();
  const environment = runtime.environment;
  const isDev = environment === 'development';

  if (isDev) {
    switch (server) {
      case Servers.LicenseAndUpdate:
        return `${runtime.licenseAndUpdateBaseUrl ?? 'http://localhost:1102'}${endpoint}`;
      case Servers.AnalyticServer:
        return `${runtime.analyticsServerURL}${endpoint}`;
      case Servers.CortexStudio:
        return `${runtime.cortexStudioUrl ?? 'http://localhost:8420'}${endpoint}`;
      default:
        return '';
    }
  }
  switch (server) {
    case Servers.LicenseAndUpdate:
      return `${runtime.licenseAndUpdateBaseUrl ?? 'https://omnibusx.com/app-api'}${endpoint}`;
    case Servers.AnalyticServer:
      return `${runtime.analyticsServerURL}${endpoint}`;
    case Servers.CortexStudio:
      return `${runtime.cortexStudioUrl ?? ''}${endpoint}`;
    default:
      return '';
  }
};

export async function get<T>(
  endpoint: string,
  server: Servers,
  params: { [key: string]: string },
  headers: Record<string, string> = {},
): Promise<T> {
  const url = new URL(buildUrl(server, endpoint));
  Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: await buildRequestHeaders(headers, {
      includeContentType: true,
      audience: audienceForServer(server),
    }),
  });

  if (!response.ok) {
    if (response.status === 401) return handleUnauthorized<T>();
    const errorData = await response.json();
    throw new Error(errorData.detail || response.statusText);
  }

  const data = await response.json();
  return data as T;
}

export async function post<T>(endpoint: string, server: Servers, data: object = {}, headers: object = {}): Promise<T> {
  const url = buildUrl(server, endpoint);

  const response = await fetch(url, {
    method: 'POST',
    headers: await buildRequestHeaders(headers as Record<string, string>, {
      includeContentType: true,
      audience: audienceForServer(server),
    }),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status === 401) return handleUnauthorized<T>();
    const errorData = await response.json();
    throw new Error(errorData.detail || response.statusText);
  }

  const responseData = await response.json();
  return responseData as T;
}

export async function postStream(
  endpoint: string,
  server: Servers,
  data: object = {},
  signal?: AbortSignal,
  headers: object = {},
): Promise<Response> {
  const url = buildUrl(server, endpoint);

  const response = await fetch(url, {
    method: 'POST',
    headers: await buildRequestHeaders(headers as Record<string, string>, {
      includeContentType: true,
      audience: audienceForServer(server),
    }),
    body: JSON.stringify(data),
    signal,
  });

  if (!response.ok) {
    if (response.status === 401) return handleUnauthorized<Response>();
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || response.statusText);
  }

  return response;
}

export async function postWithUrl<T>(url: string, data: object = {}, headers: object = {}): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: await buildRequestHeaders(headers as Record<string, string>, { includeContentType: true }),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status === 401) return handleUnauthorized<T>();
    const errorData = await response.json();
    throw new Error(errorData.detail || response.statusText);
  }

  const responseData = await response.json();
  return responseData as T;
}

export async function postUploadFile<T>(
  endpoint: string,
  server: Servers,
  file: File,
  data: object = {},
  headers: Record<string, string> = {},
): Promise<T> {
  const url = buildUrl(server, endpoint);

  const formData = new FormData();
  formData.append('file', file);
  formData.append('params', JSON.stringify(data));

  const response = await fetch(url, {
    method: 'POST',
    headers: await buildRequestHeaders(headers, {
      includeContentType: false,
      audience: audienceForServer(server),
    }),
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) return handleUnauthorized<T>();
    const errorData = await response.json();
    throw new Error(errorData.detail || response.statusText);
  }

  const responseData = await response.json();
  return responseData as T;
}

export async function postUploadBlob<T>(
  endpoint: string,
  server: Servers,
  file: Blob,
  data: object = {},
  headers: Record<string, string> = {},
): Promise<T> {
  const url = buildUrl(server, endpoint);

  const formData = new FormData();
  formData.append('file', file);
  formData.append('params', JSON.stringify(data));

  const response = await fetch(url, {
    method: 'POST',
    headers: await buildRequestHeaders(headers, {
      includeContentType: false,
      audience: audienceForServer(server),
    }),
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) return handleUnauthorized<T>();
    const errorData = await response.json();
    throw new Error(errorData.detail || response.statusText);
  }

  const responseData = await response.json();
  return responseData as T;
}
